import { createGlobalStyle } from 'styled-components';
import styled from 'styled-components';

/* ============================================================
   Design system — Editorial / Neo-brutalist
   ============================================================ */

const Colors = {
  // Nueva paleta (referencia)
  paper: '#F2EFE6',     // fondo crema / papel
  ink: '#1A0E1A',       // negro-ciruela (texto y titulares)
  oxblood: '#3A0E0E',   // vino — secciones full-bleed
  lime: '#D7F205',      // chartreuse — CTA y detalles
  lavender: '#7B6FD4',  // bloques de foto / anotaciones a mano
  muted: '#6F6B66',     // texto cuerpo sobre crema
  line: 'rgba(26,14,26,0.22)', // hairlines

  // Alias retro-compatibles (no romper imports existentes)
  primary: '#F2EFE6',
  secondary: '#3A0E0E',
  tertiary: '#3A0E0E',
  text: '#1A0E1A',
};

const fonts = {
  display: "'Anton', system-ui, sans-serif",
  body: "'Inter', system-ui, sans-serif",
  script: "'Caveat', cursive",
};

const GlobalStyle = createGlobalStyle`
  * { box-sizing: border-box; }

  html { scroll-behavior: smooth; }

  body {
    font-family: ${fonts.body};
    color: ${Colors.ink};
    background-color: ${Colors.paper};
    height: 100%;
    padding: 0;
    margin: 0 auto;
    -webkit-font-smoothing: antialiased;
    -webkit-overflow-scrolling: touch;
  }

  ::selection {
    background: ${Colors.lime};
    color: ${Colors.ink};
  }

  a { color: inherit; }
`;

/* ---------- Layout ---------- */

const ContainerMarginSections = styled.div`
  max-width: 1320px;
  margin: 0 auto;
  padding: 5rem 2rem;

  @media (max-width: 868px) {
    width: 100%;
    padding: 3rem 1.25rem;
  }
`;

// Sección oscura a sangre completa con esquinas superiores redondeadas
const DarkSection = styled.section`
  background: ${Colors.oxblood};
  color: ${Colors.paper};
  border-radius: 40px 40px 0 0;
  width: 100%;

  @media (max-width: 868px) {
    border-radius: 28px 28px 0 0;
  }
`;

/* ---------- Tipografía ---------- */

// Titular gigante condensado (Anton)
const DisplayTitle = styled.h2`
  font-family: ${fonts.display};
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  line-height: 0.92;
  margin: 0;
  color: ${Colors.ink};
  font-size: clamp(2.8rem, 8vw, 6.5rem);
`;

const ScriptAccent = styled.span`
  font-family: ${fonts.script};
  font-weight: 600;
  color: ${Colors.lavender};
  font-size: clamp(1.4rem, 3vw, 2rem);
  line-height: 1.1;
`;

// Badge tipo píldora con borde — etiqueta de sección
const SectionLabel = styled.span`
  display: inline-block;
  font-family: ${fonts.body};
  font-weight: 600;
  font-size: 0.95rem;
  color: ${Colors.ink};
  padding: 0.55rem 1.4rem;
  border: 1.5px solid ${Colors.ink};
  border-radius: 999px;
  white-space: nowrap;
`;

// Línea fina divisora
const Hairline = styled.div`
  width: 100%;
  height: 1px;
  background: ${({ $dark }) => ($dark ? 'rgba(242,239,230,0.28)' : Colors.line)};
`;

const BodyText = styled.p`
  font-family: ${fonts.body};
  color: ${Colors.muted};
  font-size: 1.05rem;
  line-height: 1.7;
  margin: 0;
  font-weight: 400;
`;

/* ---------- Alias retro-compatibles ---------- */
// Mantienen funcionando imports antiguos mientras se migra todo.

const BigTitle = DisplayTitle;
const Title = DisplayTitle;
const TitleGradient = styled(DisplayTitle)``;
const TitleGrayNumber = styled.h2`
  font-family: ${fonts.display};
  color: rgba(26, 14, 26, 0.35);
  font-size: clamp(2rem, 5vw, 4rem);
  margin: 0;
`;
const SubTitle = styled.h3`
  font-family: ${fonts.body};
  color: ${Colors.ink};
  font-size: 1.6rem;
  font-weight: 700;
  margin: 0;
`;
const Text = BodyText;

export {
  GlobalStyle,
  ContainerMarginSections,
  DarkSection,
  DisplayTitle,
  ScriptAccent,
  SectionLabel,
  Hairline,
  BodyText,
  fonts,
  Colors,
  // alias
  BigTitle,
  Title,
  TitleGradient,
  TitleGrayNumber,
  SubTitle,
  Text,
};
